// src/services/configService.ts
interface Config {
  name: string;
  apiEndpoint: string;
  /** Legacy implicit-flow login URL — rollback path while `auth_flow` rolls out. */
  redirect_url: string;
  main_page: string;
  /** Cognito hosted-UI custom domain (authorize + token endpoints live here). */
  auth_domain: string;
  client_id: string;
  /** Byte-identical in authorize + token calls. Always the host the page is
   *  on: the PKCE state lives in that origin's sessionStorage, so a login
   *  that starts on one registered host must finish on the same one. */
  oauth_redirect_uri: string;
  /** The app's previous origin (react.<env>.westernbulk.cloud): source of the
   *  one-time browser-storage import, empty where there is none. */
  legacy_origin: string;
  /** Cognito user-pool issuer — must equal the id_token `iss` claim. */
  oidc_issuer: string;
  /** 'code' = authorization-code+PKCE with silent refresh; 'implicit' = legacy. */
  auth_flow: "code" | "implicit";
  [key: string]: string;
}

const prodConfig: Config = {
  name: "prod",
  apiEndpoint: "https://api.prod.westernbulk.cloud/v1",
  redirect_url: "https://auth.prod.westernbulk.cloud/oauth2/authorize?identity_provider=LOGIN&response_type=token&client_id=52q24rpchcoct0fkv2cktqi4ie&redirect_uri=https://westernava.com&scope=aws.cognito.signin.user.admin%20email%20openid",
  main_page: "https://westernava.com/",
  auth_domain: "https://auth.prod.westernbulk.cloud",
  client_id: "52q24rpchcoct0fkv2cktqi4ie",
  oauth_redirect_uri: "https://westernava.com",
  legacy_origin: "https://react.prod.westernbulk.cloud",
  oidc_issuer: "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_stDWbKD1s",
  auth_flow: "code",
  // Add other prod-specific settings here
};

const testConfig: Config = {
  name: "test",
  apiEndpoint: "https://api.sandbox.westernbulk.cloud/v1",
  redirect_url: "https://auth.sandbox.westernbulk.cloud/oauth2/authorize?identity_provider=LOGIN&response_type=token&client_id=13b613ntlkph5s8mncslosvlot&redirect_uri=https://sandbox.westernava.com&scope=aws.cognito.signin.user.admin%20email%20openid",
  main_page: "https://sandbox.westernava.com/",
  auth_domain: "https://auth.sandbox.westernbulk.cloud",
  client_id: "13b613ntlkph5s8mncslosvlot",
  oauth_redirect_uri: "https://sandbox.westernava.com",
  legacy_origin: "https://react.sandbox.westernbulk.cloud",
  oidc_issuer: "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_KzFECJaVv",
  auth_flow: "code",
  // Add other test-specific settings here
};

const localConfig: Config = {
  name: "local",
  apiEndpoint: "https://api.sandbox.westernbulk.cloud/v1",
  redirect_url: "https://auth.sandbox.westernbulk.cloud/oauth2/authorize?identity_provider=LOGIN&response_type=token&client_id=13b613ntlkph5s8mncslosvlot&redirect_uri=https://sandbox.westernava.com&scope=aws.cognito.signin.user.admin%20email%20openid",
  main_page: "https://sandbox.westernava.com/",
  auth_domain: "https://auth.sandbox.westernbulk.cloud",
  client_id: "13b613ntlkph5s8mncslosvlot",
  oauth_redirect_uri: "https://sandbox.westernava.com",
  legacy_origin: "",
  oidc_issuer: "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_KzFECJaVv",
  auth_flow: "code", // moot on localhost (TOKEN constant short-circuits auth)
  // Add other local-specific settings here
};

/** Exact hosts; anything else (personal CloudFront domains) is sandbox. */
const CONFIG_BY_HOST: Record<string, Config> = {
  "westernava.com": prodConfig,
  "react.prod.westernbulk.cloud": prodConfig,
  "sandbox.westernava.com": testConfig,
  "react.sandbox.westernbulk.cloud": testConfig,
};

const onOwnHost = (config: Config, hostname: string): Config => {
  const origin = `https://${hostname}`;
  return {
    ...config,
    oauth_redirect_uri: origin,
    redirect_url: config.redirect_url.replace(config.oauth_redirect_uri, origin),
  };
};

export const configForHost = (hostname: string): Config => {
  if (hostname.includes("localhost")) {
    return localConfig;
  }
  const config = CONFIG_BY_HOST[hostname];
  return config ? onOwnHost(config, hostname) : testConfig;
};

export const getConfig = (): Config => configForHost(window.location.hostname);
